<template>
    <div class="section-form">
        <!-- Filtros de Data e Tipo -->
        <div class="row">
            <div class="col-md-3">
                <div class="form-group">
                    <label for="retreat_date">
                        <span class="required">*</span>
                        Data de Retirada
                    </label>
                    <CtkDateTimePicker
                        :value="value.RetreatDatetime"
                        @input="updateRetreatDate"
                        :color="$store.state.date_picker_color"
                        locale="pt-br"
                        no-label
                        format="YYYY-MM-DD HH:mm:ss"
                        button-now-translation="Hoje"
                        class="datetimepicker"
                        label="Selecione a data e hora de retirada"
                        id="retreat_date"
                        :minute-interval="60"
                        :min-minute="0"
                        :max-minute="0"
                        :min-date="minDate"
                        :max-date="maxDate"
                    />
                    <div v-if="nextWeekBlocked" class="alert alert-info text-center mt-2 mb-0 next-week-notice">
                        <i class="fas fa-info-circle mr-2"></i>
                        <small>As reservas para a próxima semana só são liberadas a partir das 07h00 de segunda-feira.</small>
                    </div>
                </div>
            </div>

            <div class="col-md-3">
                <div class="form-group">
                    <label for="devolution_date">
                        <span class="required">*</span>
                        Data de Devolução
                    </label>
                    <CtkDateTimePicker
                        :value="value.DevolutionDatetime"
                        @input="updateDevolutionDate"
                        :color="$store.state.date_picker_color"
                        locale="pt-br"
                        no-label
                        format="YYYY-MM-DD HH:mm:ss"
                        button-now-translation="Hoje"
                        class="datetimepicker"
                        label="Selecione a data e hora de devolução"
                        id="devolution_date"
                        :minute-interval="60"
                        :min-minute="0"
                        :max-minute="0"
                        :min-date="minDevolutionDate || minDate"
                        :max-date="maxDevolutionDate || maxDate"
                    />
                </div>
            </div>

            <!-- Cidade Destino (condicional) -->
            <div class="col-md-3" v-if="$store.state.user.inform_city_on_reservation">
                <div class="form-group">
                    <label for="city_destination">
                        <span class="required">*</span>
                        Cidade Destino
                    </label>
                    <v-select
                        :options="cityDestinations"
                        label="description"
                        :reduce="(city) => city.id"
                        class="style-chooser"
                        taggable
                        v-model="value.cityDestination_id"
                        @input="updateCityDestination"
                    >
                        <div slot="no-options">Nenhuma cidade encontrada</div>
                    </v-select>
                </div>
            </div>

            <div class="col-md-3">
                <div class="form-group">
                    <label for="vehicle_type">
                        <span class="required">*</span>
                        Tipo do Veículo
                    </label>
                    <v-select
                        :options="vehicleTypes"
                        :reduce="(vehicle_type_id) => vehicle_type_id.id"
                        label="description"
                        class="style-chooser"
                        :value="value.vehicle_type_id"
                        @input="updateVehicleType"
                    >
                        <div slot="no-options">Carregando...</div>
                    </v-select>
                </div>
            </div>
        </div>

        <!-- Justificativa e Motivo -->
        <div class="row">
            <div class="col-md-4">
                <div class="form-group">
                    <label for="justification"><span class="required">*</span> Justificativa</label>
                    <v-select
                        :options="justifications"
                        label="description"
                        :reduce="(justification) => justification.id"
                        class="style-chooser"
                        :value="value.justification_id"
                        @input="updateJustification"
                    >
                        <div slot="no-options">Carregando...</div>
                    </v-select>
                </div>
            </div>

            <div class="col-md-8">
                <div class="form-group">
                    <label for="reason">
                        <span class="required">*</span>
                        Motivo da Reserva
                    </label>
                    <input
                        class="form-control p-2"
                        id="reason"
                        :value="value.reason"
                        @input="updateReason"
                    />
                </div>
            </div>
        </div>

        <!-- KM e Filial -->
        <div class="row">
            <div class="col-md-3" v-if="!$store.state.user.inform_city_on_reservation">
                <div class="form-group">
                    <label for="km_provided">
                        <span class="required">*</span>
                        Quantos KM pretende rodar?
                    </label>
                    <input
                        type="number"
                        class="form-control"
                        :value="value.km_provided"
                        @input="updateKmProvided"
                        placeholder="0"
                    />
                </div>
            </div>

            <div class="col-md-6">
                <div class="form-group">
                    <label for="branch">
                        <span class="required">*</span>
                        Filial - Cidade de saída
                    </label>
                    <v-select
                        :options="branches"
                        label="description"
                        :reduce="(branch) => branch.id"
                        class="style-chooser"
                        :value="value.branch_id"
                        @input="updateBranch"
                    >
                        <div slot="no-options">Nenhuma filial vinculada</div>
                    </v-select>
                </div>
            </div>

            <!-- Centro de Custo (condicional) -->
            <div class="col-md-3" v-if="!value.network_diagram">
                <div class="form-group">
                    <label for="cost_center">
                        <span class="required">*</span>
                        Centro de Custo
                    </label>
                    <v-select
                        :options="costCenters"
                        label="erp_code"
                        class="style-chooser"
                        :reduce="(cost_center) => cost_center.id"
                        :value="value.cost_center_id"
                        @input="updateCostCenter"
                    >
                        <div slot="no-options">Carregando...</div>
                    </v-select>
                </div>
            </div>

            <!-- Diagrama de Rede (condicional) -->
            <div class="col-md-3" v-if="!value.cost_center_id">
                <div class="form-group">
                    <label for="network_diagram">
                        <span class="required">*</span>
                        Diagrama de Rede
                    </label>
                    <v-select
                        :options="networkDiagrams"
                        label="erp_code"
                        class="style-chooser"
                        :reduce="(network_diagram) => network_diagram.id"
                        :value="value.network_diagram"
                        @input="updateNetworkDiagram"
                    >
                        <div slot="no-options">Carregando...</div>
                    </v-select>
                </div>
            </div>
        </div>

        <!-- Botão de Pesquisa -->
        <div class="row">
            <div class="col-md-12 mt-auto">
                <div class="form-group text-end">
                    <button
                        type="button"
                        class="btn btn-search"
                        @click="$emit('search-vehicles')"
                        :disabled="!canSearch"
                    >
                        <i class="fa fa-car mr-2"></i>
                        Pesquisar Veículos
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import vSelect from "vue-select";
import CtkDateTimePicker from "vue-ctk-date-time-picker";

export default {
    name: 'VehicleSearchFilters',
    components: {
        vSelect,
        CtkDateTimePicker,
    },
    props: {
        value: {
            type: Object,
            required: true
        },
        cityDestinations: {
            type: Array,
            default: () => []
        },
        vehicleTypes: {
            type: Array,
            default: () => []
        },
        justifications: {
            type: Array,
            default: () => []
        },
        branches: {
            type: Array,
            default: () => []
        },
        costCenters: {
            type: Array,
            default: () => []
        },
        networkDiagrams: {
            type: Array,
            default: () => []
        },
        drivers: {
            type: Array,
            default: () => []
        },
        rides: {
            type: Array,
            default: () => []
        },
        minDate: {
            type: String,
            default: ''
        },
        maxDate: {
            type: String,
            default: ''
        },
        nextWeekBlocked: {
            type: Boolean,
            default: false
        },
        minDevolutionDate: {
            type: String,
            default: ''
        },
        maxDevolutionDate: {
            type: String,
            default: ''
        },
        isSearchingDriver: {
            type: Boolean,
            default: false
        },
        isSearchingRide: {
            type: Boolean,
            default: false
        },
        isLoadingDriverSearch: {
            type: Boolean,
            default: false
        },
        isLoadingRideSearch: {
            type: Boolean,
            default: false
        },
        driverRegistration: {
            type: String,
            default: ''
        },
        rideRegistration: {
            type: String,
            default: ''
        }
    },
    computed: {
        // Verifica se pode realizar a pesquisa
        canSearch() {
            return (
                this.value.RetreatDatetime &&
                this.value.DevolutionDatetime &&
                this.value.vehicle_type_id &&
                this.value.branch_id &&
                this.value.reason &&
                (this.value.cost_center_id || this.value.network_diagram)
            );
        }
    },
    methods: {
        // Métodos de atualização dos campos
        updateValue(field, newValue) {
            this.$emit('input', { ...this.value, [field]: newValue });
        },

        updateRetreatDate(newValue) {
            this.updateValue('RetreatDatetime', newValue);
            this.$emit('retreat-date-change');
        },

        updateDevolutionDate(newValue) {
            this.updateValue('DevolutionDatetime', newValue);
            this.$emit('close-vehicles-table');
        },

        updateCityDestination(newValue) {
            this.updateValue('cityDestination_id', newValue);
        },

        updateVehicleType(newValue) {
            this.updateValue('vehicle_type_id', newValue);
            this.$emit('close-vehicles-table');
        },

        updateJustification(newValue) {
            this.updateValue('justification_id', newValue);
            this.$emit('close-vehicles-table');
        },

        updateReason(event) {
            this.updateValue('reason', event.target.value);
        },

        updateKmProvided(event) {
            this.updateValue('km_provided', event.target.value);
            this.updateValue('vehicle_type_id', '');
            this.$emit('get-vehicle-types');
        },

        updateBranch(newValue) {
            this.updateValue('branch_id', newValue);
            this.$emit('close-vehicles-table');
        },

        updateCostCenter(newValue) {
            this.updateValue('cost_center_id', newValue);
        },

        updateNetworkDiagram(newValue) {
            this.updateValue('network_diagram', newValue);
        },
    }
};
</script>

<style scoped>


.form-group {
    margin-bottom: 1rem;
}

.form-group label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
}

.required {
    color: #dc3545;
    font-weight: bold;
}

.form-control {
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 0.375rem 0.75rem;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.btn-search {
    background: linear-gradient(135deg, #FE8D18 0%, #0056b3 100%);
    border: none;
    color: white;
    padding: 10px 20px;
    border-radius: 5px;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);
}

.btn-search:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.4);
    background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
}

.btn-search:disabled {
    background: #6c757d;
    cursor: not-allowed;
    opacity: 0.65;
    box-shadow: none;
}

.btn-search:disabled:hover {
    transform: none;
}

.style-chooser {
    border-radius: 4px;
}

.datetimepicker {
    width: 100%;
}

.text-end {
    text-align: right;
}

@media (max-width: 768px) {
    .section-form {
        padding: 15px;
    }

    .text-end {
        text-align: center;
    }
}

/* Customização do v-select */
.style-chooser >>> .vs__dropdown-toggle {
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 2px 6px;
}

.style-chooser >>> .vs__dropdown-toggle:hover {
    border-color: #80bdff;
}

.style-chooser >>> .vs__selected {
    color: #495057;
}

.style-chooser >>> .vs__search::placeholder {
    color: #6c757d;
}
</style>



// WEBPACK FOOTER //
// src/components/ReserveVehicles/add-components/VehicleSearchFilters.vue